import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { format, formatDistanceToNow } from "date-fns";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { AdminGuard } from "@/components/role-guard";
import { TableSkeleton } from "@/components/skeletons";
import { SubscriptionBypassForm } from "@/components/subscription-bypass-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Heading } from "@/components/ui/typography";
import { trpc } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type OverrideRecord =
  inferRouterOutputs<AppRouter>["admin"]["listSubscriptionOverrides"][number];
type BillingSummary = inferRouterOutputs<AppRouter>["admin"]["billingSummary"];
type SubscriberRecord = BillingSummary["subscribers"][number];

export default function AdminBillingOversight() {
  const summaryQuery = useQuery({
    ...trpc.admin.billingSummary.queryOptions(),
    placeholderData: (previousData) => previousData,
  });

  return (
    <AdminGuard>
      <PageContainer className="flex flex-col gap-6">
        <PageHeader
          title="Billing oversight"
          description="Review existing billing accounts, subscription mix, and temporary override usage."
        />

        <BillingSummaryCards
          summary={summaryQuery.data}
          isLoading={summaryQuery.isLoading}
          isError={summaryQuery.isError}
        />

        <SubscribedUsersTable
          summary={summaryQuery.data}
          isLoading={summaryQuery.isLoading}
          isError={summaryQuery.isError}
        />

        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <BillingMixCard
            summary={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
            isError={summaryQuery.isError}
          />
          <UpcomingExpiryCard
            summary={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
            isError={summaryQuery.isError}
          />
        </div>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-col gap-2">
              <div>
                <CardTitle>Overrides &amp; pilots</CardTitle>
                <CardDescription>
                  Surface manual subscription bypasses and the admins who
                  granted them.
                </CardDescription>
              </div>
              <SubscriptionBypassForm />
            </div>
          </CardHeader>
          <CardContent>
            <OverridesTable />
          </CardContent>
        </Card>
      </PageContainer>
    </AdminGuard>
  );
}

function BillingSummaryCards({
  summary,
  isLoading,
  isError,
}: {
  summary: BillingSummary | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isError) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        Failed to load billing summary. Please try again later.
      </div>
    );
  }

  const cards = [
    {
      label: "Billing accounts",
      value: summary?.totals.billingAccounts ?? 0,
      helper: "Accounts currently linked to a manager.",
    },
    {
      label: "Linked sites",
      value: summary?.totals.linkedSites ?? 0,
      helper: "Sites attached to billing accounts.",
    },
    {
      label: "Active overrides",
      value: summary?.totals.activeOverrides ?? 0,
      helper: "Managers currently bypassing billing enforcement.",
    },
    {
      label: "Scheduled expiries",
      value: summary?.totals.scheduledOverrideExpiries ?? 0,
      helper: "Overrides with an expiry date recorded.",
    },
  ];

  return (
    <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <Card key={card.label}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {card.label}
            </CardTitle>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-3xl font-semibold text-foreground">
                {card.value}
              </div>
            )}
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {card.helper}
          </CardContent>
        </Card>
      ))}
    </section>
  );
}

function BillingMixCard({
  summary,
  isLoading,
  isError,
}: {
  summary: BillingSummary | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Subscription snapshot</CardTitle>
        <CardDescription>
          Current subscriptions grouped by status and plan using the existing
          billing records.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <DistributionTable
          title="By status"
          rows={
            summary?.subscriptionsByStatus.map((row) => ({
              label: startCase(row.status),
              value: row.count,
            })) ?? []
          }
          isLoading={isLoading}
          isError={isError}
          emptyLabel="No subscriptions recorded."
        />
        <DistributionTable
          title="By plan"
          rows={
            summary?.subscriptionsByPlan.map((row) => ({
              label: startCase(row.planId),
              value: row.count,
            })) ?? []
          }
          isLoading={isLoading}
          isError={isError}
          emptyLabel="No plan allocations recorded."
        />
      </CardContent>
    </Card>
  );
}

function DistributionTable({
  title,
  rows,
  isLoading,
  isError,
  emptyLabel,
}: {
  title: string;
  rows: Array<{ label: string; value: number }>;
  isLoading: boolean;
  isError: boolean;
  emptyLabel: string;
}) {
  return (
    <section className="space-y-3">
      <Heading variant="subsection" as="h3">
        {title}
      </Heading>
      {isError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-4 text-sm text-destructive">
          Failed to load this billing breakdown.
        </p>
      ) : isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
          {emptyLabel}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{title}</TableHead>
              <TableHead className="text-right">Count</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell className="text-foreground">{row.label}</TableCell>
                <TableCell className="text-right text-foreground">
                  {row.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

function SubscribedUsersTable({
  summary,
  isLoading,
  isError,
}: {
  summary: BillingSummary | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const rows = useMemo(
    () => summary?.subscribers ?? [],
    [summary?.subscribers]
  );
  const [selectedSubscriber, setSelectedSubscriber] =
    useState<SubscriberRecord | null>(null);

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Subscribed users</CardTitle>
          <CardDescription>
            Billing account owners, their current subscription state, and the
            sites linked to each account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              Unable to load subscribed users.
            </div>
          ) : isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
              No subscribed users have been recorded yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Sites</TableHead>
                  <TableHead>Renewal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={`${row.billingAccountId}-${row.subscriptionId ?? "none"}`}
                  >
                    <TableCell>
                      <div className="flex flex-col items-start">
                        <Button
                          variant="link"
                          className="h-auto p-0 font-medium text-foreground"
                          onClick={() => setSelectedSubscriber(row)}
                        >
                          {row.name}
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {row.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.company ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{startCase(row.planId)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getSubscriptionStatusVariant(
                          row.subscriptionStatus
                        )}
                      >
                        {startCase(row.subscriptionStatus)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-foreground">
                      {row.linkedSites}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatSubscriptionRenewal(row)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SubscriberDetailsDialog
        subscriber={selectedSubscriber}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedSubscriber(null);
          }
        }}
      />
    </>
  );
}

function SubscriberDetailsDialog({
  subscriber,
  onOpenChange,
}: {
  subscriber: SubscriberRecord | null;
  onOpenChange: (open: boolean) => void;
}) {
  const resync = useMutation({
    ...trpc.admin.resyncSubscriptionQuantity.mutationOptions(),
    onSuccess: (data) => {
      if (data.synced) {
        toast.success(`Subscription quantity resynced to ${data.quantity}`);
      } else {
        toast.success(
          "No sync needed — account has no active per-site subscription"
        );
      }
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Resync failed: ${message}`);
    },
  });

  const detailRows = subscriber
    ? [
        {
          label: "Email",
          value: subscriber.email,
          monospace: false,
        },
        {
          label: "Company",
          value: subscriber.company ?? "—",
          monospace: false,
        },
        {
          label: "Linked sites",
          value: String(subscriber.linkedSites),
          monospace: false,
        },
        {
          label: "Renewal",
          value: formatSubscriptionRenewal(subscriber),
          monospace: false,
        },
        {
          label: "Current period end",
          value: formatSubscriptionPeriodEnd(subscriber.currentPeriodEnd),
          monospace: false,
        },
        {
          label: "Provider",
          value: startCase(subscriber.provider),
          monospace: false,
        },
        {
          label: "Billing account ID",
          value: subscriber.billingAccountId,
          monospace: true,
        },
        {
          label: "Owner user ID",
          value: subscriber.ownerUserId ?? "—",
          monospace: true,
        },
        {
          label: "Subscription ID",
          value: subscriber.subscriptionId ?? "—",
          monospace: true,
        },
        {
          label: "Customer ID",
          value: subscriber.providerCustomerId,
          monospace: true,
        },
      ]
    : [];

  return (
    <Dialog open={Boolean(subscriber)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {subscriber ? subscriber.name : "Subscription details"}
          </DialogTitle>
          <DialogDescription>
            Review the existing billing account and subscription metadata for
            this user.
          </DialogDescription>
        </DialogHeader>

        {subscriber ? (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{startCase(subscriber.planId)}</Badge>
              <Badge
                variant={getSubscriptionStatusVariant(
                  subscriber.subscriptionStatus
                )}
              >
                {startCase(subscriber.subscriptionStatus)}
              </Badge>
              {subscriber.cancelAtPeriodEnd ? (
                <Badge variant="warning">Cancels at period end</Badge>
              ) : null}
            </div>

            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-56">Field</TableHead>
                    <TableHead>Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailRows.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell className="font-medium text-foreground">
                        {row.label}
                      </TableCell>
                      <TableCell
                        className={`break-words text-muted-foreground ${row.monospace ? "font-meta text-xs" : "text-sm"}`}
                      >
                        {row.value}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {subscriber.subscriptionId ? (
              <div className="flex items-center gap-3 border-t pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={resync.isPending}
                  onClick={() =>
                    resync.mutate({
                      billingAccountId: subscriber.billingAccountId,
                    })
                  }
                >
                  {resync.isPending ? "Resyncing…" : "Resync quantity"}
                </Button>
                <span className="text-xs text-muted-foreground">
                  Reconciles the Stripe subscription seat count with the current
                  active site count for this account.
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function UpcomingExpiryCard({
  summary,
  isLoading,
  isError,
}: {
  summary: BillingSummary | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const rows = useMemo(
    () => summary?.upcomingOverrideExpiries ?? [],
    [summary?.upcomingOverrideExpiries]
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Upcoming override expiries</CardTitle>
        <CardDescription>
          Active overrides that already have an expiry date recorded.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            Unable to load upcoming expiries.
          </div>
        ) : isLoading ? (
          <>
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
            No active overrides have an expiry date set.
          </div>
        ) : (
          rows.map((override) => {
            const expiry = formatExpiry(override.expiresAt);

            return (
              <div
                key={override.managerId}
                className="rounded-md border px-3 py-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {override.managerName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {override.managerEmail}
                    </p>
                    {override.reason ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {override.reason}
                      </p>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-foreground">
                      {expiry.label}
                    </p>
                    {expiry.relative ? (
                      <p className="text-xs text-muted-foreground">
                        {expiry.relative}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

function OverridesTable() {
  const queryClient = useQueryClient();
  const overridesOptions = trpc.admin.listSubscriptionOverrides.queryOptions();
  const overridesQuery = useQuery({
    ...overridesOptions,
    placeholderData: (previousData) => previousData,
  });
  const overrides = (overridesQuery.data ?? []) as OverrideRecord[];

  const [selectedOverride, setSelectedOverride] =
    useState<OverrideRecord | null>(null);

  const revokeOverride = useMutation({
    ...trpc.admin.setSubscriptionOverride.mutationOptions(),
    onSuccess: () => {
      toast.success("Override revoked");
      queryClient.invalidateQueries({ queryKey: overridesOptions.queryKey });
      queryClient.invalidateQueries({
        queryKey: trpc.admin.summary.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.admin.billingSummary.queryKey(),
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(message);
    },
  });

  const handleConfirmRevoke = async () => {
    if (!selectedOverride) {
      return;
    }
    try {
      await revokeOverride.mutateAsync({
        managerEmail: selectedOverride.managerEmail,
        enabled: false,
      });
      setSelectedOverride(null);
    } catch (_error) {
      // Error toast is handled in onError; keep dialog open for retry.
    }
  };

  if (overridesQuery.isError) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        Failed to load overrides. Please try again later.
      </div>
    );
  }

  if (overridesQuery.isLoading) {
    return <TableSkeleton columns={5} />;
  }

  if (overrides.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-muted p-6 text-center text-sm text-muted-foreground">
        No active overrides or pilots at the moment.
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="space-y-3 md:hidden">
          {overrides.map((override) => (
            <OverrideCard
              key={override.managerId}
              override={override}
              onRevoke={() => setSelectedOverride(override)}
              revokePending={
                revokeOverride.isPending &&
                selectedOverride?.managerId === override.managerId
              }
            />
          ))}
        </div>

        <div className="table-panel hidden md:block">
          <Table data-testid="admin-overrides-table">
            <TableHeader>
              <TableRow>
                <TableHead className="px-4">Manager</TableHead>
                <TableHead className="px-4">Email</TableHead>
                <TableHead className="px-4">Granted by</TableHead>
                <TableHead className="px-4">Reason</TableHead>
                <TableHead className="px-4 text-right">Expires</TableHead>
                <TableHead className="px-4 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {overrides.map((override) => (
                <OverrideRow
                  key={override.managerId}
                  override={override}
                  onRevoke={() => setSelectedOverride(override)}
                  revokePending={
                    revokeOverride.isPending &&
                    selectedOverride?.managerId === override.managerId
                  }
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog
        open={Boolean(selectedOverride)}
        onOpenChange={(open) => {
          if (!open && !revokeOverride.isPending) {
            setSelectedOverride(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke override</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedOverride
                ? `Remove billing bypass access for ${selectedOverride.managerName}?`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeOverride.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={revokeOverride.isPending}
              onClick={handleConfirmRevoke}
            >
              {revokeOverride.isPending ? "Revoking…" : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function OverrideRow({
  override,
  onRevoke,
  revokePending,
}: {
  override: OverrideRecord;
  onRevoke: () => void;
  revokePending: boolean;
}) {
  const expiry = formatExpiry(override.expiresAt);

  return (
    <TableRow>
      <TableCell className="px-4 py-3 text-sm font-medium text-foreground">
        <span className="truncate block max-w-[12rem]">
          {override.managerName}
        </span>
      </TableCell>
      <TableCell className="px-4 py-3 text-sm text-muted-foreground">
        <span className="truncate block max-w-[16rem]">
          {override.managerEmail}
        </span>
      </TableCell>
      <TableCell className="px-4 py-3 text-sm text-muted-foreground">
        {override.grantedBy ? (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">
              {override.grantedBy.name}
            </span>
            <span className="text-xs text-muted-foreground">
              {override.grantedBy.email}
            </span>
          </div>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell className="px-4 py-3 text-sm text-muted-foreground">
        <span className="truncate block max-w-[14rem]">
          {override.reason ?? "—"}
        </span>
        {override.notes ? (
          <span className="mt-1 block text-xs text-muted-foreground">
            {override.notes}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="px-4 py-3 text-right text-sm text-muted-foreground">
        <div className="flex flex-col items-end">
          <span>{expiry.label}</span>
          {expiry.relative ? (
            <span className="text-xs text-muted-foreground">
              {expiry.relative}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="px-4 py-3 text-right">
        <Button
          variant="outline"
          size="sm"
          disabled={revokePending}
          onClick={onRevoke}
        >
          Revoke
        </Button>
      </TableCell>
    </TableRow>
  );
}

function OverrideCard({
  override,
  onRevoke,
  revokePending,
}: {
  override: OverrideRecord;
  onRevoke: () => void;
  revokePending: boolean;
}) {
  const expiry = formatExpiry(override.expiresAt);

  return (
    <div className="surface-panel space-y-3 p-4">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">
          {override.managerName}
        </p>
        <p className="text-xs text-muted-foreground break-all">
          {override.managerEmail}
        </p>
      </div>

      <div className="text-sm text-muted-foreground space-y-1">
        <p>
          <span className="font-medium text-foreground">Granted by: </span>
          {override.grantedBy?.name ?? "—"}
        </p>
        {override.grantedBy?.email ? (
          <p className="text-xs break-all">{override.grantedBy.email}</p>
        ) : null}
      </div>

      <div className="text-sm text-muted-foreground space-y-1">
        <p>
          <span className="font-medium text-foreground">Reason: </span>
          {override.reason ?? "—"}
        </p>
        {override.notes ? <p className="text-xs">{override.notes}</p> : null}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Expires: </span>
          {expiry.label}
          {expiry.relative ? (
            <span className="ml-2 text-xs text-muted-foreground">
              ({expiry.relative})
            </span>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={revokePending}
          onClick={onRevoke}
        >
          Revoke
        </Button>
      </div>
    </div>
  );
}

function formatExpiry(value: string | null): {
  label: string;
  relative: string | null;
} {
  if (!value) {
    return { label: "—", relative: null };
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { label: "—", relative: null };
  }
  return {
    label: format(date, "d MMM yyyy"),
    relative: formatDistanceToNow(date, { addSuffix: true }),
  };
}

function formatSubscriptionPeriodEnd(value: string | null) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return `${format(date, "d MMM yyyy, HH:mm")} (${formatDistanceToNow(date, {
    addSuffix: true,
  })})`;
}

function formatSubscriptionRenewal(subscriber: SubscriberRecord) {
  if (!subscriber.currentPeriodEnd) {
    return "—";
  }

  const date = new Date(subscriber.currentPeriodEnd);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  const relative = formatDistanceToNow(date, { addSuffix: true });
  return subscriber.cancelAtPeriodEnd ? `Ends ${relative}` : relative;
}

function getSubscriptionStatusVariant(status: string) {
  if (status === "active") {
    return "approved" as const;
  }

  if (status === "past_due") {
    return "warning" as const;
  }

  return "outline" as const;
}

function startCase(value: string) {
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}
